import type { SVGProps } from "react";

/**
 * Lightweight inline SVG icon set, no icon-font dependency, crisp at any
 * size, inherits `currentColor`. Stroke-based line icons for a clean,
 * modern industrial look. Add new icons to the `paths` map as needed.
 */

export type IconName =
  | "blade"
  | "core"
  | "floor-saw"
  | "road"
  | "hand-saw"
  | "joint"
  | "pipe"
  | "wall"
  | "remove"
  | "arrow-right"
  | "arrow-up"
  | "arrow-up-right"
  | "phone"
  | "mail"
  | "check"
  | "shield"
  | "clock"
  | "pin"
  | "camera"
  | "ruler"
  | "home"
  | "building"
  | "truck"
  | "spark"
  | "industrial"
  | "menu"
  | "close"
  | "plus"
  | "minus"
  | "chevron-down";

interface IconProps extends SVGProps<SVGSVGElement> {
  name: IconName;
  size?: number;
}

const paths: Record<IconName, JSX.Element> = {
  blade: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="2.4" />
      <path d="M12 3.5v3M12 17.5v3M3.5 12h3M17.5 12h3M6 6l2.1 2.1M15.9 15.9L18 18M18 6l-2.1 2.1M8.1 15.9L6 18" />
    </>
  ),
  core: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 3.5v4M12 16.5v4" />
    </>
  ),
  "floor-saw": (
    <>
      <circle cx="8.5" cy="14.5" r="4.5" />
      <path d="M8.5 14.5h9l3-6" />
      <path d="M14 5.5l3.5 3.5" />
    </>
  ),
  road: (
    <>
      <path d="M6 20L9 4h6l3 16" />
      <path d="M12 6v2M12 11v2M12 16v2" />
    </>
  ),
  "hand-saw": (
    <>
      <path d="M4 8l12 3 4 4-2 2-4-4-9-2z" />
      <path d="M15 11l3-5 2 1-3 5" />
    </>
  ),
  joint: (
    <>
      <path d="M4 8h16M4 16h16" />
      <path d="M12 4v4M12 16v4" />
    </>
  ),
  pipe: (
    <>
      <path d="M4 9a3 3 0 0 1 3-3h4v6H7a3 3 0 0 1-3-3z" />
      <path d="M11 12h6a3 3 0 0 1 0 6h-3" />
    </>
  ),
  wall: (
    <>
      <rect x="3.5" y="4.5" width="17" height="15" rx="1" />
      <path d="M9.5 19.5v-7a2.5 2.5 0 0 1 5 0v7" />
    </>
  ),
  remove: (
    <>
      <path d="M4 18h16" />
      <path d="M6 18l1.5-6 4 2 2-4 3 3 1.5 5" />
    </>
  ),
  "arrow-right": <path d="M4 12h15M13 6l6 6-6 6" />,
  "arrow-up": <path d="M12 20V5M6 11l6-6 6 6" />,
  "arrow-up-right": <path d="M7 17L17 7M8 7h9v9" />,
  phone: (
    <path d="M6.5 4h3l1.5 4-2 1.5a12 12 0 0 0 5.5 5.5l1.5-2 4 1.5v3a2 2 0 0 1-2 2A16 16 0 0 1 4.5 6a2 2 0 0 1 2-2z" />
  ),
  mail: (
    <>
      <rect x="3.5" y="5.5" width="17" height="13" rx="1.5" />
      <path d="M4 7l8 6 8-6" />
    </>
  ),
  check: <path d="M4 12.5l5 5L20 6" />,
  shield: <path d="M12 3.5l7 2.5v5c0 4.5-3 7.8-7 9.5-4-1.7-7-5-7-9.5V6z" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21c4-4.5 7-7.6 7-11a7 7 0 1 0-14 0c0 3.4 3 6.5 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  camera: (
    <>
      <path d="M4 8h3l1.5-2h7L17 8h3v11H4z" />
      <circle cx="12" cy="13" r="3.2" />
    </>
  ),
  ruler: (
    <>
      <rect x="3" y="7" width="18" height="10" rx="1" transform="rotate(0 12 12)" />
      <path d="M7 7v3M11 7v4M15 7v3M19 7v4" />
    </>
  ),
  home: <path d="M4 11l8-6.5L20 11M6 10v9h12v-9" />,
  building: (
    <>
      <rect x="5" y="3.5" width="14" height="17" rx="1" />
      <path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" />
    </>
  ),
  truck: (
    <>
      <path d="M3 7h11v8H3zM14 10h4l3 3v2h-7z" />
      <circle cx="7" cy="17.5" r="1.8" />
      <circle cx="17.5" cy="17.5" r="1.8" />
    </>
  ),
  spark: <path d="M12 3l2 6 6 2-6 2-2 6-2-6-6-2 6-2z" />,
  industrial: (
    <>
      <path d="M3 20v-8l4 2.5V12l4 2.5V12l4 2.5V6h2v14z" />
      <path d="M6 17h1.5M10 17h1.5M14 17h1.5" />
    </>
  ),
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  "chevron-down": <path d="M5 9l7 7 7-7" />,
};

export function Icon({ name, size = 24, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {paths[name]}
    </svg>
  );
}
